import { useEffect, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useReceiverStore } from '../store/receiverStore';
import { getRoomStatus } from '../lib/api';
import { TransferProgress } from '../components/TransferProgress';
import { IntegrityBadge, PhaseBadge } from '../components/Badges';
import { formatBytes } from '../lib/util/format';

export function SharePage() {
  const { roomId = '' } = useParams();
  const {
    phase,
    meta,
    stats,
    needsSaveLocation,
    canStreamToDisk,
    hasResumable,
    integrity,
    downloadUrl,
    downloadName,
    error,
    connect,
    chooseSaveLocation,
    cancel,
  } = useReceiverStore();

  // Poll room presence until the transfer is underway.
  const { data: status } = useQuery({
    queryKey: ['room', roomId],
    queryFn: () => getRoomStatus(roomId),
    enabled: Boolean(roomId),
    refetchInterval: phase === 'idle' || phase === 'connecting' ? 3000 : false,
  });

  useEffect(() => {
    if (roomId) connect(roomId);
    return () => useReceiverStore.getState().reset();
    // connect is stable from the store; run once per room.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  const roomMissing = status?.exists === false;

  return (
    <div className="space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
          Incoming transfer
        </h1>
        <p className="text-sm text-slate-400">Room {roomId}</p>
      </div>

      {roomMissing && (
        <Note tone="error">
          This transfer link is invalid or has expired. Ask the sender for a new link.
        </Note>
      )}

      <section className="space-y-5 rounded-2xl border border-white/10 bg-ink-900/50 p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-slate-100">
              {meta?.name ?? 'Waiting for file details…'}
            </h2>
            {meta && <p className="text-sm text-slate-400">{formatBytes(meta.size)}</p>}
          </div>
          <PhaseBadge phase={phase} />
        </div>

        {phase === 'connecting' && !meta && (
          <p className="text-sm text-slate-400">
            {status?.online === false
              ? 'Connecting… the sender does not appear to be online yet.'
              : 'Connecting to the sender…'}
          </p>
        )}

        {needsSaveLocation && meta && (
          <div className="space-y-3 rounded-xl bg-ink-950/60 p-4 ring-1 ring-white/5">
            <p className="text-sm font-medium text-slate-200">
              {hasResumable
                ? 'A partial download was found. Resume saving to disk?'
                : 'Where should we save this file?'}
            </p>
            <div className="flex flex-wrap gap-3">
              {canStreamToDisk && (
                <button
                  onClick={() => chooseSaveLocation('disk')}
                  className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-400"
                >
                  {hasResumable ? 'Resume to disk' : 'Stream to disk'}
                </button>
              )}
              <button
                onClick={() => chooseSaveLocation('memory')}
                className="rounded-xl border border-white/10 px-4 py-2 text-sm text-slate-200 transition hover:bg-white/5"
              >
                Keep in memory{!canStreamToDisk && ' (recommended for this browser)'}
              </button>
            </div>
            {canStreamToDisk ? (
              <p className="text-xs text-slate-500">
                Streaming to disk supports very large files with minimal memory use.
              </p>
            ) : (
              <p className="text-xs text-amber-300/80">
                This browser can’t stream to disk; large files may exhaust memory.
              </p>
            )}
          </div>
        )}

        {(phase === 'transferring' || phase === 'verifying' || phase === 'complete') && (
          <TransferProgress stats={stats} />
        )}

        {phase === 'complete' && (
          <div className="space-y-3 rounded-xl bg-emerald-500/10 p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-emerald-200">Download ready</span>
              <IntegrityBadge result={integrity} />
            </div>
            {downloadUrl ? (
              <a
                href={downloadUrl}
                download={downloadName ?? meta?.name}
                className="inline-block rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-400"
              >
                Save {downloadName ?? meta?.name}
              </a>
            ) : (
              <p className="text-sm text-emerald-200/90">
                Saved to the location you chose on disk.
              </p>
            )}
          </div>
        )}

        {integrity === 'corrupted' && (
          <Note tone="error">
            Integrity check failed — the received file does not match the sender’s hash. Ask the
            sender to retry.
          </Note>
        )}

        {error && <Note tone="error">{error}</Note>}

        {(phase === 'transferring' || phase === 'negotiating') && (
          <button
            onClick={cancel}
            className="rounded-xl border border-white/10 px-4 py-2 text-sm text-slate-300 transition hover:bg-white/5"
          >
            Cancel
          </button>
        )}
      </section>
    </div>
  );
}

function Note({ tone, children }: { tone: 'error' | 'info'; children: ReactNode }) {
  const cls =
    tone === 'error' ? 'bg-rose-500/10 text-rose-300' : 'bg-sky-500/10 text-sky-300';
  return <p className={`rounded-xl px-4 py-3 text-sm ${cls}`}>{children}</p>;
}
